import { useState, useEffect } from 'react';
import { setAnalyticsCookies, COOKIE_TYPES } from './cookie';

/**
 * Custom hook for managing cookie consent and preferences
 * Provides methods to show/hide cookie popups and settings, and manage preferences
 */
export const useCookieConsent = () => {
  const [showCookiePopup, setShowCookiePopup] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [cookiePreferences, setCookiePreferencesState] = useState({
    necessary: true, // Always enabled
    functional: true,
    analytics: true,
  });

  // Helper to get cookie value
  const getCookie = (name) => {
    try {
      const match = document.cookie.match(new RegExp(`(^|;)\\s*${name}=([^;]+)`));
      return match ? decodeURIComponent(match[2]) : null;
    } catch (e) {
      return null;
    }
  };

  // Helper to set a cookie
  const setCookie = (name, value, days) => {
    try {
      let expires = '';
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/';
    } catch (e) {
      // ignore cookie write errors
    }
  };

  // Delete a cookie
  const deleteCookie = (name) => {
    try {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    } catch (e) {
      // ignore
    }
  };

  // Initialize cookie consent state on mount
  useEffect(() => {
    const cookiePrefs = getCookie('cookiePreferences');
    if (!cookiePrefs) {
      setShowCookiePopup(true);
    } else {
      try {
        const prefs = JSON.parse(cookiePrefs);
        setCookiePreferencesState(prefs);
      } catch (e) {
        setShowCookiePopup(true);
      }
    }
  }, []);

  const handleCookieAccept = () => {
    // Accept all cookies except analytics
    const prefs = {
      necessary: true,
      functional: true,
      analytics: false,
    };
    setCookiePreferencesState(prefs);
    setCookie('cookiePreferences', JSON.stringify(prefs), 365);
    setCookie('cookieConsent', 'true', 365);
    setShowCookiePopup(false);
  };

  const handleCookieReject = () => {
    // Only necessary cookies
    const prefs = {
      necessary: true,
      functional: false,
      analytics: false,
    };
    setCookiePreferencesState(prefs);
    setCookie('cookiePreferences', JSON.stringify(prefs), 365);
    setCookie('cookieConsent', 'false', 365);
    // Delete optional cookies
    deleteCookie('language');
    deleteCookie('_ga');
    deleteCookie('_gid');
    setShowCookiePopup(false);
  };

  const handleOpenSettings = () => {
    setShowCookieSettings(true);
  };

  const handleCloseSettings = () => {
    setShowCookieSettings(false);
  };

  const handleSettingChange = (type) => {
    if (type === 'necessary') return; // Cannot change necessary cookies
    setCookiePreferencesState(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleConfirmSettings = () => {
    // Save preferences
    setCookie('cookiePreferences', JSON.stringify(cookiePreferences), 365);
    setCookie('cookieConsent', 'true', 365);

    // Clean up cookies based on preferences
    if (!cookiePreferences.functional) {
      deleteCookie('language');
    }
    if (!cookiePreferences.analytics) {
      deleteCookie('_ga');
      deleteCookie(COOKIE_TYPES.GA_SESSION);
    } else {
      // If analytics is enabled, set GA cookies immediately
      setAnalyticsCookies();
    }

    setShowCookieSettings(false);
    setShowCookiePopup(false);
  };

  const handleClosePopupButton = () => {
    setShowCookiePopup(false);
  };

  return {
    // State
    showCookiePopup,
    showCookieSettings,
    cookiePreferences,
    
    // Methods to manage popup
    setShowCookiePopup,
    setShowCookieSettings,
    
    // Handlers
    handleCookieAccept,
    handleCookieReject,
    handleOpenSettings,
    handleCloseSettings,
    handleSettingChange,
    handleConfirmSettings,
    handleClosePopupButton,
    
    // Update preferences directly
    setCookiePreferencesState,
  };
};
